import * as THREE from 'three';
import { Water } from 'three/examples/jsm/objects/Water.js';
import { Sky } from 'three/examples/jsm/objects/Sky.js';
import type { GameAssets } from './AssetLoader';
import { TOWER_POSITION } from '@shared/constants';

// ============================================================
// Scene setup — above the sea, sunset
// ============================================================

export interface GameScene {
  readonly scene: THREE.Scene;
  readonly water: Water;
  readonly sun: THREE.Vector3;
}

export function createGameScene(assets: GameAssets): GameScene {
  const scene = new THREE.Scene();

  // ── Procedural Sky (sunset) ───────────────────────────────
  const sky = new Sky();
  sky.scale.setScalar(50000);
  scene.add(sky);

  const sun = new THREE.Vector3();
  const skyUniforms = sky.material.uniforms;
  skyUniforms['turbidity'].value = 20;
  skyUniforms['rayleigh'].value = 4;
  skyUniforms['mieCoefficient'].value = 0.1;
  skyUniforms['mieDirectionalG'].value = 0.99;

  const phi = THREE.MathUtils.degToRad(90 - 1.5);
  const theta = THREE.MathUtils.degToRad(180);
  sun.setFromSphericalCoords(1, phi, theta);
  skyUniforms['sunPosition'].value.copy(sun);

  // ── Fog (subtle, doesn't darken the sky) ───────────────────
  scene.fog = new THREE.FogExp2(0xf0a060, 0.00008);

  // ── Lighting (warm sunset) ────────────────────────────────
  const ambientLight = new THREE.AmbientLight(0xffc8a0, 0.4);
  scene.add(ambientLight);

  const sunLight = new THREE.DirectionalLight(0xff8844, 2.0);
  sunLight.position.copy(sun).multiplyScalar(1000);
  scene.add(sunLight);

  const hemisphereLight = new THREE.HemisphereLight(0xff9966, 0x334488, 0.5);
  scene.add(hemisphereLight);

  // ── Ocean ─────────────────────────────────────────────────
  const waterGeometry = new THREE.CircleGeometry(2500, 64);
  const water = new Water(waterGeometry, {
    textureWidth: 256,
    textureHeight: 256,
    waterNormals: new THREE.TextureLoader().load(
      'https://threejs.org/examples/textures/waternormals.jpg',
      (texture) => {
        texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
      }
    ),
    sunDirection: sun.clone().normalize(),
    sunColor: 0xff8844,
    waterColor: 0x001e0f,
    distortionScale: 3.7,
    fog: true,
  });
  water.rotation.x = -Math.PI / 2;
  water.position.y = -2;
  scene.add(water);

  // ── Target Ship (aircraft carrier) ────────────────────────
  const ship = assets.carrier.clone();
  // Auto-scale to ~300m length
  const shipBox = new THREE.Box3().setFromObject(ship);
  const shipLength = Math.max(
    shipBox.max.x - shipBox.min.x,
    shipBox.max.z - shipBox.min.z,
  );
  if (shipLength > 0) {
    const targetLength = 600;
    ship.scale.multiplyScalar(targetLength / shipLength);
  }
  ship.position.set(TOWER_POSITION.x, 35, TOWER_POSITION.z);
  ship.name = 'target-ship';
  scene.add(ship);

  return { scene, water, sun };
}
